import React, { useEffect, useRef, useState } from "react";

import classNames from "classnames";
import PropTypes from "prop-types";
import Container from "react-bootstrap/Container";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import { Mousewheel, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/scss";
import "swiper/scss/navigation";

import BrowserDetection from "../../utils/browserDetection";
import scrollActiveNavItem from "../../utils/scrollActiveNavItem";

import "./subNavigation.scss";

const SubNavigation = ({
  items,
  variant = null,
}) => {
  const scrollMenu = useRef(null);
  const [swiper, setSwiper] = useState(null);
  const [scrollMenuClasses, setScrollMenuClasses] = useState(null);
  const [isScrollable, setIsScrollable] = useState(null);
  const [nextDisabled, setNextDisabled] = useState(true);
  const [prevDisabled, setPrevDisabled] = useState(true);
  const updateSwiperControlls = () => {
    if (swiper) {
      setNextDisabled(swiper.isEnd);
      setPrevDisabled(swiper.isBeginning);
    }
  };
  const isMobile = BrowserDetection.hasTouchScreen();
  const isVariant = variant !== null;

  const focusSlide = () => {
    if (swiper) {
      const i = swiper.slides.findIndex((slide) => (
        slide.childNodes[0].classList.contains("active")
      ));

      if (i > 0 && i < swiper.slidesGrid.length) {
        swiper.clickedIndex = i;
        swiper.slideTo(i, 300);
        swiper.translateTo(
          (swiper.slidesGrid[i]
            - (window.innerWidth / 2)
            + (swiper.slidesSizesGrid[i] / 2)) * -1,
          300,
        );
      }

      updateSwiperControlls();
    }
  };

  const initSwiper = () => {
    if (swiper) {
      setTimeout(() => {
        swiper.update();
        focusSlide();
      }, 500);
    }
  };

  const checkScrollable = () => {
    if (!scrollMenu && !scrollMenu.current) return null;

    const target = isMobile
      ? scrollMenu.current.firstElementChild
      : scrollMenu.current.firstElementChild.firstElementChild;

    let classes = "";

    if (target.scrollWidth > target.clientWidth) classes = "is-scrollable";

    setIsScrollable(classes);

    return null;
  };

  const scrollTrigger = () => {
    if ((!scrollMenu && !scrollMenu.current) || !isMobile) return null;

    const target = scrollMenu.current.firstElementChild;
    const classes = [];

    if (target.scrollLeft > 20) {
      classes.push("menu-scrolling--left");
    }

    if (
      target.scrollWidth > target.clientWidth
      && target.scrollLeft + target.clientWidth < target.scrollWidth
    ) {
      classes.push("menu-scrolling--right");
    }

    const classesString = classes.join(" ");

    if (scrollMenuClasses !== classesString) {
      setScrollMenuClasses(classesString);
    }

    return null;
  };

  useEffect(() => {
    if (variant !== "tabnavigation") scrollTrigger();
    checkScrollable();

    if (typeof window !== "undefined") {
      window.addEventListener("resize", () => checkScrollable());
    }
  });

  if (isMobile) {
    useEffect(() => {
      if (scrollMenu && scrollMenu.current) {
        const nav = scrollMenu.current.firstChild;

        nav.scrollLeft = scrollActiveNavItem(nav);

        scrollTrigger();
      }
    }, []);

    return (
      <div className={classNames(
        "subnavigation__container container--fullwidth",
        { [`subnavigation__container--${variant}`]: isVariant },
      )}
      >
        <Container fluid>
          <Navbar
            className={classNames(
              "subnavigation",
              scrollMenuClasses,
              isScrollable,
              {
                "subnavigation--is-mobile": isMobile,
                [`subnavigation--${variant}`]: isVariant,
              },
            )}
            ref={scrollMenu}
          >
            <Nav
              onScroll={variant !== "tabnavigation"
                ? (() => {
                  scrollTrigger();
                })
                : null}
            >
              <div className="navbar-nav__inner">
                {items}
              </div>
            </Nav>
          </Navbar>
        </Container>
      </div>
    );
  }

  return (
    <Navbar
      expand={null}
      className={classNames(
        "subnavigation subnavigation__container container--fullwidth",
        scrollMenuClasses,
        isScrollable,
      )}
    >
      <Container fluid>
        <Nav
          ref={scrollMenu}
        >
          <Swiper
            modules={[Navigation, Mousewheel]}
            navigation={{
              nextEl: ".swiper-button-next",
              prevEl: ".swiper-button-prev",
            }}
            mousewheel={{ forceToAxis: true, invert: true }}
            noSwiping
            slidesPerView="auto"
            spaceBetween={24}
            onResize={focusSlide}
            onSetTranslate={updateSwiperControlls}
            onSwiper={setSwiper}
            onAfterInit={initSwiper}
          >
            {
              items.map((item) => (
                <SwiperSlide key={item.key}>
                  {item}
                </SwiperSlide>
              ))
            }
          </Swiper>

          <button
            className={classNames(
              "swiper-button-prev",
              { "swiper-button-disabled": prevDisabled },
            )}
            onClick={() => {
              swiper.slidePrev();
            }}
            type="button"
            aria-label="Prev"
          />

          <button
            className={classNames(
              "swiper-button-next",
              { "swiper-button-disabled": nextDisabled },
            )}
            onClick={() => {
              swiper.slideNext();
            }}
            type="button"
            aria-label="Next"
          />
        </Nav>
      </Container>
    </Navbar>
  );
};

SubNavigation.propTypes = {
  items: PropTypes.oneOfType([PropTypes.array]).isRequired,
  variant: PropTypes.oneOf(["tabnavigation"]),
};

export default SubNavigation;
